import React, { useCallback, useState, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
type ToastType = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}
interface ToastContextType {
  showToast: (type: ToastType, title: string, description?: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((type: ToastType, title: string, description?: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, {
      id,
      type,
      title,
      description
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
    warning: AlertTriangle
  };
  const colors = {
    success: 'border-green-500/50 bg-green-500/10 text-green-400',
    error: 'border-red-500/50 bg-red-500/10 text-red-400',
    info: 'border-blue-500/50 bg-blue-500/10 text-blue-400',
    warning: 'border-orange-500/50 bg-orange-500/10 text-orange-400'
  };
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-445">
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md" data-id="element-446">
        {toasts.map(toast => {
        const Icon = icons[toast.type];
        return <div key={toast.id} className={cn('rounded-lg border p-4 shadow-lg backdrop-blur-sm animate-in slide-in-from-right', colors[toast.type])} data-id="element-447">
              <div className="flex items-start gap-3" data-id="element-448">
                <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" data-id="element-449" />
                <div className="flex-1 min-w-0" data-id="element-450">
                  <p className="font-semibold text-sm text-white" data-id="element-451">
                    {toast.title}
                  </p>
                  {toast.description && <p className="text-xs text-gray-300 mt-1" data-id="element-452">
                      {toast.description}
                    </p>}
                </div>
                <button onClick={() => removeToast(toast.id)} className="text-gray-400 hover:text-white transition-colors" data-id="element-453">
                  <X className="h-4 w-4" data-id="element-454" />
                </button>
              </div>
            </div>;
      })}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}